import { ArrowRight, RotateCcw } from 'lucide-react'
import { Button } from '../ui/button'

interface ErrorPageProps {
  /** Title text, defaults to "Oops!" */
  title?: string
  /** Subtitle text, defaults to "Something went wrong" */
  subtitle?: string
  /** Description text */
  description?: string
  /** Show the background shape */
  showBackground?: boolean
  /** Callback for retry button */
  onRetry?: () => void
  /** Render prop for home link */
  renderHomeLink?: (props: { className: string; children: React.ReactNode }) => React.ReactNode
  /** Label for home link button */
  homeLinkLabel?: string
}

export function ErrorPage({
  title = 'Oops!',
  subtitle = 'Something went wrong',
  description = "We're sorry, but something unexpected happened. Please try again.",
  showBackground = true,
  onRetry,
  renderHomeLink,
  homeLinkLabel = 'Go to homepage',
}: ErrorPageProps) {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-white px-4 overflow-hidden">
      {/* Background Shape */}
      {showBackground && (
        <svg
          className="absolute pointer-events-none fill-unsw-yellow"
          viewBox="0 0 60 60"
          aria-hidden="true"
          style={{ top: 0, left: '-16%', height: '55vw' }}
        >
          <polygon points="0 0 0 48.533488 39.4213208 60 57.4153242 35.7397558 32.0559823 0" />
        </svg>
      )}

      <div className="relative z-10 text-center">
        <h1 className="mb-2 text-6xl font-bold text-gray-500">{title}</h1>
        <h2 className="mb-4 text-2xl font-semibold text-gray-800">{subtitle}</h2>
        <p className="mb-8 text-gray-600">{description}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {onRetry && (
            <Button
              onClick={onRetry}
              variant="outline"
              className="border-black text-black hover:bg-black hover:text-white [letter-spacing:1px] py-6 text-base font-mono font-normal"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Try again
            </Button>
          )}
          {renderHomeLink ? (
            renderHomeLink({
              className:
                'inline-flex items-center justify-center border border-black text-black hover:bg-black hover:text-white [letter-spacing:1px] py-6 px-4 text-base font-mono font-normal transition-colors',
              children: (
                <>
                  {homeLinkLabel}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              ),
            })
          ) : (
            <Button
              asChild
              variant="outline"
              className="border-black text-black hover:bg-black hover:text-white [letter-spacing:1px] py-6 text-base font-mono font-normal"
            >
              <a href="/">
                {homeLinkLabel}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
